<template>
  <!-- Outside the auth-gated block on purpose: whoever has this tab open --
       mid-task in the app, sitting on the login screen, or stuck on a
       workspace error -- should find out a new version shipped either way. -->
  <UpdateAvailableBanner />

  <template v-if="authReady">
    <LoginPage v-if="!isAuthenticated" />
    <PortalWorkspaceError
      v-else-if="workspaceBootstrapError"
      :message="workspaceBootstrapError"
      :retrying="workspaceBootstrapping"
      :signing-out="workspaceFailureSigningOut"
      @logout="handleWorkspaceLogout"
      @retry="runWorkspaceBootstrap"
    />
    <PortalWorkspaceLoader v-else-if="!workspaceReady" />
    <router-view v-else />
  </template>
</template>

<script>
import { mapState } from 'vuex'
import LoginPage from '@/views/MainPages/LoginPage.vue'
import PortalWorkspaceLoader from '@/components/Extras/PortalWorkspaceLoader.vue'
import PortalWorkspaceError from '@/components/Extras/PortalWorkspaceError.vue'
import UpdateAvailableBanner from '@/components/Extras/UpdateAvailableBanner.vue'
import { bootstrapWorkspace } from '@/utils/workspaceBootstrap'
import { forcePortalLogout } from '@/utils/authSessionRecovery'

export default {
  name: 'App',
  components: {
    LoginPage,
    PortalWorkspaceLoader,
    PortalWorkspaceError,
    UpdateAvailableBanner,
  },
  data() {
    return {
      workspaceFailureSigningOut: false,
    }
  },
  computed: {
    ...mapState([
      'authReady',
      'isAuthenticated',
      'workspaceReady',
      'workspaceBootstrapping',
      'workspaceBootstrapError',
    ]),
  },
  watch: {
    isAuthenticated: {
      immediate: true,
      handler(isAuthenticated) {
        if (isAuthenticated) {
          this.runWorkspaceBootstrap()
          return
        }
        this.$store.commit('RESET_WORKSPACE')
      },
    },
  },
  methods: {
    async handleWorkspaceLogout() {
      if (this.workspaceFailureSigningOut) return
      this.workspaceFailureSigningOut = true
      try {
        await forcePortalLogout(this.$store, { router: this.$router })
      } finally {
        this.workspaceFailureSigningOut = false
      }
    },
    async runWorkspaceBootstrap() {
      if (!this.isAuthenticated || this.workspaceReady) return
      this.$store.commit('SET_WORKSPACE_BOOTSTRAP_ERROR', '')
      try {
        await bootstrapWorkspace(this.$store, {
          router: this.$router,
        })
      } catch {
        // Error message is stored in vuex for PortalWorkspaceError.
      }
    },
  },
}
</script>

<style>
/* ── Global base: typography & smooth rendering ── */
#app {
  --y-field-height: 1.875rem;

  font-family: var(--y-font-sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--y-text);
  background: var(--y-bg);
}

/* ── Compact form inputs (same layout as v-select) ── */
#app .v-text-field--density-compact,
#app .v-select--density-compact,
#app .v-autocomplete--density-compact,
.user-dialog-card .v-text-field,
.user-dialog-card .v-select,
.user-dialog-card .v-autocomplete {
  --v-input-control-height: var(--y-field-height);
}

#app .v-field--density-compact,
.user-dialog-card .v-field {
  --v-field-input-padding-top: 0;
  --v-field-input-padding-bottom: 0;
  font-size: var(--y-field-font-size, 0.8125rem);
}

/* Field shell */
#app .v-text-field--density-compact .v-field,
#app .v-select--density-compact .v-field,
#app .v-autocomplete--density-compact .v-field,
.user-dialog-card .v-text-field .v-field,
.user-dialog-card .v-select .v-field,
.user-dialog-card .v-autocomplete .v-field,
.table-filter-select .v-field {
  min-height: var(--y-field-height) !important;
  font-size: var(--y-field-font-size, 0.8125rem);
  border-radius: var(--y-radius-sm) !important;
}

/* Inner row — center like v-select */
#app .v-text-field--density-compact .v-field__field,
#app .v-select--density-compact .v-field__field,
#app .v-autocomplete--density-compact .v-field__field,
.user-dialog-card .v-text-field .v-field__field,
.user-dialog-card .v-select .v-field__field,
.user-dialog-card .v-autocomplete .v-field__field,
.table-filter-select .v-field__field {
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

/* Input area — center like v-select */
#app .v-text-field--density-compact .v-field__input,
#app .v-select--density-compact .v-field__input,
#app .v-autocomplete--density-compact .v-field__input,
.user-dialog-card .v-text-field .v-field__input,
.user-dialog-card .v-select .v-field__input,
.user-dialog-card .v-autocomplete .v-field__input,
.table-filter-select .v-field__input {
  display: flex;
  align-items: center;
  min-height: var(--y-field-height) !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: var(--y-field-font-size, 0.8125rem);
  line-height: 1.25;
}

/* Outlined states — stop Vuetify extra vertical padding on text fields */
#app .v-field--variant-outlined.v-field--density-compact .v-field__input,
#app .v-field--variant-outlined.v-field--density-compact.v-field--active .v-field__input,
#app .v-field--variant-outlined.v-field--density-compact.v-field--dirty .v-field__input,
#app .v-field--variant-outlined.v-field--density-compact.v-field--focused .v-field__input,
.user-dialog-card .v-field--variant-outlined .v-field__input,
.user-dialog-card .v-field--variant-outlined.v-field--active .v-field__input,
.user-dialog-card .v-field--variant-outlined.v-field--dirty .v-field__input,
.user-dialog-card .v-field--variant-outlined.v-field--focused .v-field__input {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* v-text-field & v-autocomplete native input */
#app .v-text-field--density-compact .v-field__input input,
#app .v-autocomplete--density-compact .v-field__input input,
.user-dialog-card .v-text-field .v-field__input input,
.user-dialog-card .v-autocomplete .v-field__input input {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0 !important;
  height: auto !important;
  align-self: center;
  line-height: 1.25;
  padding: 0 !important;
  margin: 0 !important;
  border: 0;
  outline: none;
  background: transparent;
  font-size: inherit;
}

/* v-select & v-autocomplete displayed value */
#app .v-select--density-compact .v-select__selection,
#app .v-autocomplete--density-compact .v-autocomplete__selection,
.user-dialog-card .v-select .v-select__selection,
.user-dialog-card .v-autocomplete .v-autocomplete__selection,
.table-filter-select .v-select__selection {
  display: flex;
  align-items: center;
  min-height: 0;
  margin: 0;
  line-height: 1.25;
}

#app .v-select--density-compact .v-select__selection-text,
#app .v-autocomplete--density-compact .v-autocomplete__selection-text,
.user-dialog-card .v-select .v-select__selection-text,
.user-dialog-card .v-autocomplete .v-autocomplete__selection-text {
  line-height: 1.25;
}

/* Icons */
#app .v-field--density-compact .v-field__prepend-inner,
#app .v-field--density-compact .v-field__append-inner,
#app .v-field--density-compact .v-field__clearable,
.user-dialog-card .v-field .v-field__prepend-inner,
.user-dialog-card .v-field .v-field__append-inner,
.user-dialog-card .v-field .v-field__clearable {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  align-self: center;
  align-items: center;
}

#app .v-field--density-compact .v-field__prepend-inner > .v-icon,
#app .v-field--density-compact .v-field__append-inner > .v-icon,
.user-dialog-card .v-field .v-field__prepend-inner > .v-icon,
.user-dialog-card .v-field .v-field__append-inner > .v-icon {
  font-size: 16px;
}

#app .v-field--variant-outlined.v-field--density-compact .v-label,
.user-dialog-card .v-field--variant-outlined .v-label {
  font-size: var(--y-field-font-size, 0.8125rem);
}

#app .v-input--density-compact .v-input__details {
  min-height: 12px;
  padding-top: 2px;
  font-size: 0.6875rem;
}

/* v-textarea — vertically centered content (matches compact fields) */
#app .v-textarea--density-compact .v-field,
.user-dialog-card .v-textarea .v-field {
  min-height: 72px !important;
  max-height: none;
}

#app .v-textarea--density-compact .v-field__field,
.user-dialog-card .v-textarea .v-field__field {
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

#app .v-textarea--density-compact .v-field__input,
.user-dialog-card .v-textarea .v-field__input {
  display: flex;
  align-items: center;
  min-height: 72px !important;
  padding: var(--y-space-2) var(--y-space-3) !important;
  line-height: 1.5;
}

#app .v-textarea--density-compact .v-field__input textarea,
.user-dialog-card .v-textarea .v-field__input textarea {
  align-self: center;
  width: 100%;
  line-height: 1.5;
  padding: 0 !important;
  margin: 0 !important;
  resize: vertical;
}

#app .v-textarea--density-compact .v-field--variant-outlined .v-field__input,
#app .v-textarea--density-compact .v-field--variant-outlined.v-field--active .v-field__input,
#app .v-textarea--density-compact .v-field--variant-outlined.v-field--dirty .v-field__input,
#app .v-textarea--density-compact .v-field--variant-outlined.v-field--focused .v-field__input,
.user-dialog-card .v-textarea .v-field--variant-outlined .v-field__input {
  padding-top: var(--y-space-2) !important;
  padding-bottom: var(--y-space-2) !important;
}

/* ── App shell: top bar (dark glass — matches login theme) ── */
.portal-app .app-bar.v-toolbar {
  position: relative;
  z-index: 2;
}

.portal-app .app-bar.v-toolbar .v-toolbar__content {
  height: 100%;
  padding-inline: var(--y-space-4) var(--y-space-6);
}

.portal-app .app-bar {
  background: var(--y-app-bar-bg) !important;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--y-app-bar-border) !important;
  box-shadow: none !important;
}

.portal-app .app-bar-title {
  font-size: var(--y-font-size-md);
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--y-app-bar-title);
}

.app-bar {
  background: rgb(var(--v-theme-surface)) !important;
  border-bottom: 1px solid var(--y-border) !important;
  box-shadow: none !important;
}

.app-bar-title {
  font-size: var(--y-font-size-md);
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--y-text);
}

.app-bar-search {
  flex: 1;
  max-width: 380px;
  min-width: 200px;
}

.app-bar-search .v-field {
  min-height: 30px;
  border-radius: var(--y-radius-sm);
  transition: background var(--y-transition-fast), box-shadow var(--y-transition-fast);
}

.app-bar-search .v-field__field {
  align-items: center;
}

.app-bar-search .v-field__input {
  display: flex;
  align-items: center;
  color: var(--y-text);
  font-size: var(--y-font-size-sm);
  padding-top: 0;
  padding-bottom: 0;
  min-height: 30px;
  line-height: 1.25;
}

.app-bar-search .v-field__input input {
  align-self: center;
  line-height: 1.25;
  padding: 0;
}

.app-bar-search .v-icon {
  color: var(--y-text-muted);
}

/* ── Table page toolbar ── */
.portal-app .table-toolbar {
  flex-shrink: 0;
  padding: var(--y-space-1) var(--y-space-3);
  margin-bottom: var(--y-space-1);
  background: var(--y-panel-bg);
  border: 1px solid var(--y-panel-border);
  border-radius: var(--y-radius-md);
}

.portal-app .table-toolbar__count {
  font-size: var(--y-font-size-sm);
  font-weight: 500;
  color: var(--y-toolbar-count);
  letter-spacing: 0.01em;
}

.portal-app .table-toolbar__count strong {
  color: var(--y-toolbar-count-strong);
  font-weight: 600;
}

.table-toolbar {
  flex-shrink: 0;
  padding: var(--y-space-1) var(--y-space-3);
  margin-bottom: var(--y-space-1);
  background: var(--y-surface);
  border: 1px solid var(--y-border);
  border-radius: var(--y-radius-md);
}

.table-toolbar__count {
  font-size: var(--y-font-size-sm);
  font-weight: 500;
  color: var(--y-text-secondary);
  letter-spacing: 0.01em;
}

.table-toolbar__count strong {
  color: var(--y-text);
  font-weight: 600;
}

/* ── Dialog overlay & cards — theme-aware glass ── */
.v-overlay:has(.user-dialog-card) .v-overlay__scrim {
  background: var(--y-dialog-scrim) !important;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.user-dialog-card {
  overflow: hidden;
  background: var(--y-dialog-bg) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--y-dialog-border) !important;
  box-shadow: var(--y-dialog-shadow) !important;
  color: var(--y-text);
}

.user-dialog-card .dialog-header {
  padding: var(--y-space-2) var(--y-space-4) var(--y-space-2);
  background: var(--y-dialog-header-bg);
  border-bottom: 1px solid var(--y-dialog-border);
}

.user-dialog-card .dialog-header__title {
  font-size: var(--y-font-size-lg);
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--y-text);
}

.user-dialog-card .dialog-close-btn {
  color: var(--y-dialog-close) !important;
}

.user-dialog-card .dialog-close-btn:hover:not(:disabled) {
  color: var(--y-text) !important;
  background: var(--y-dialog-close-hover-bg) !important;
}

.user-dialog-card .dialog-body {
  padding: var(--y-space-2) var(--y-space-4);
  color: var(--y-dialog-body-text);
}

.user-dialog-card .dialog-body--message {
  font-size: var(--y-font-size-sm);
  line-height: var(--y-line-height-normal);
}

.user-dialog-card .dialog-body__name {
  color: var(--y-text);
  font-weight: 600;
}

.user-dialog-card .dialog-body .v-row {
  margin-top: -4px;
  margin-bottom: -4px;
}

.user-dialog-card .dialog-body .v-col {
  padding-top: 4px;
  padding-bottom: 4px;
}

.user-dialog-card .dialog-footer {
  padding: var(--y-space-1) var(--y-space-4);
  background: var(--y-dialog-footer-bg);
  border-top: 1px solid var(--y-dialog-border);
  position: sticky;
  bottom: 0;
}

.user-dialog-card .v-card-title {
  letter-spacing: -0.01em;
}

.user-dialog-card .dialog-footer .v-btn,
.user-dialog-card .dialog-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.user-dialog-card .dialog-btn--muted {
  color: var(--y-dialog-muted-btn) !important;
}

.user-dialog-card .dialog-btn--muted:hover:not(:disabled) {
  color: var(--y-text) !important;
  background: var(--y-dialog-close-hover-bg) !important;
}

.user-dialog-card .dialog-btn--danger {
  background: rgba(239, 68, 68, 0.2) !important;
  color: #fca5a5 !important;
  border: 1px solid rgba(248, 113, 113, 0.35) !important;
}

.user-dialog-card .dialog-btn--danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.35) !important;
  color: var(--y-text) !important;
}

.user-dialog-card .btn-primary-cta.v-btn {
  background: var(--y-btn-primary-gradient) !important;
  color: #ffffff !important;
  box-shadow: var(--y-btn-primary-shadow) !important;
}

.user-dialog-card .btn-primary-cta.v-btn:hover:not(:disabled) {
  filter: brightness(1.06);
  box-shadow: var(--y-btn-primary-shadow-hover) !important;
}

/* Dialog form fields */
.user-dialog-card .v-field {
  background: var(--y-field-bg);
  color: var(--y-field-text);
}

.user-dialog-card .v-field__outline {
  --v-field-border-opacity: 0.45;
  color: var(--y-field-border);
}

.user-dialog-card .v-field--focused .v-field__outline {
  color: var(--y-field-focus-border);
  --v-field-border-opacity: 1;
}

.user-dialog-card .v-field--focused {
  box-shadow: var(--y-field-focus-ring);
}

.user-dialog-card .v-field__input,
.user-dialog-card input,
.user-dialog-card textarea {
  color: var(--y-field-text);
}

.user-dialog-card input::placeholder,
.user-dialog-card textarea::placeholder {
  color: var(--y-field-placeholder);
  opacity: 1;
}

.user-dialog-card .v-label {
  color: var(--y-field-label) !important;
}

.user-dialog-card .v-field .v-field__prepend-inner .v-icon,
.user-dialog-card .v-field .v-field__append-inner .v-icon {
  color: var(--y-field-icon);
  opacity: 1;
}

.user-dialog-card .v-field--focused .v-field__prepend-inner .v-icon {
  color: var(--y-field-focus-icon);
}

.user-dialog-card .v-messages__message {
  color: #fca5a5;
}

/* Solo / solo-filled fields in portaled dialogs */
.user-dialog-card .v-field--variant-solo,
.user-dialog-card .v-field--variant-solo-filled {
  background: var(--y-field-bg) !important;
  color: var(--y-field-text);
}

.user-dialog-card .v-field--variant-solo .v-field__overlay,
.user-dialog-card .v-field--variant-solo-filled .v-field__overlay {
  opacity: 0 !important;
}

.user-dialog-card .v-field--variant-solo-filled.v-field--focused,
.user-dialog-card .v-field--variant-solo.v-field--focused {
  box-shadow: var(--y-field-focus-ring);
}

.user-dialog-card .v-textarea.v-field--variant-solo-filled .v-field,
.user-dialog-card .v-textarea.v-field--variant-solo .v-field {
  background: var(--y-field-bg) !important;
}

.user-dialog-card .v-textarea.v-field--variant-solo-filled .v-field__input textarea,
.user-dialog-card .v-textarea.v-field--variant-solo .v-field__input textarea {
  color: var(--y-field-text);
}

/* ── Reusable data-table page layout ── */
.table-page {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--y-space-1);
}

.table-container {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-radius: var(--y-radius-lg);
}

.table-container:not(.table-container--with-toolbar) {
  flex-shrink: 0;
}

.table-container--with-toolbar {
  flex: 1 1 auto;
  min-height: 0;
}

/* ── Data table: polished admin dashboard ── */
.portal-app .table-container .app-data-table.v-data-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--y-table-border);
  border-radius: var(--y-radius-lg);
  background: var(--y-table-bg);
  overflow: hidden;
}

.table-container .app-data-table.v-data-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--y-border);
  border-radius: var(--y-radius-lg);
  background: var(--y-surface);
  overflow: hidden;
}

.table-container .app-data-table > .v-table__wrapper {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  height: auto !important;
}

.table-container .app-data-table > .v-divider,
.table-container .app-data-table > .v-data-table-footer {
  flex-shrink: 0;
}

.table-container .app-data-table .v-table__wrapper > table {
  table-layout: fixed;
  width: 100%;
}

/* Header row: opaque sticky layer so scrolled body text never bleeds through */
.portal-app .table-container .app-data-table .v-table__wrapper {
  isolation: isolate;
}

.portal-app .table-container .app-data-table thead {
  position: sticky;
  top: 0;
  z-index: 12;
  background: var(--y-table-header-bg-solid) !important;
}

.portal-app .table-container .app-data-table thead th,
.portal-app .table-container .app-data-table .v-table thead th {
  position: sticky;
  top: 0;
  z-index: 12;
  padding: 0 var(--y-space-2) !important;
  height: var(--y-table-header-height) !important;
  min-height: var(--y-table-header-height) !important;
  font-size: 0.6875rem !important;
  font-weight: 600 !important;
  line-height: var(--y-table-header-height) !important;
  text-transform: uppercase;
  letter-spacing: var(--y-letter-spacing-label);
  color: var(--y-table-header-text) !important;
  background: var(--y-table-header-bg-solid) !important;
  background-color: var(--y-table-header-bg-solid) !important;
  background-image: none !important;
  border-bottom: 1px solid var(--y-border) !important;
  white-space: nowrap;
  vertical-align: middle;
  overflow: visible;
  box-shadow: var(--y-table-header-shadow);
}

.portal-app .table-container .app-data-table thead th,
.portal-app .table-container .app-data-table thead th .v-data-table-header__content,
.portal-app .table-container .app-data-table thead th .v-data-table-header__content span,
.portal-app .table-container .app-data-table .v-table thead th .v-data-table-header__content,
.portal-app .table-container .app-data-table thead th.text-medium-emphasis,
.portal-app .table-container .app-data-table thead th .text-medium-emphasis,
.portal-app .table-container .app-data-table thead th .v-data-table-column--sorted,
.portal-app .table-container .app-data-table thead th .v-data-table-header__sort-badge {
  color: var(--y-table-header-text) !important;
  -webkit-text-fill-color: var(--y-table-header-text) !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.portal-app .table-container .app-data-table thead th .v-icon {
  color: var(--y-table-header-text) !important;
  opacity: 1 !important;
}

.portal-app .table-container .app-data-table.v-data-table thead,
.portal-app .table-container .app-data-table.v-data-table thead tr,
.portal-app .table-container .app-data-table.v-data-table thead th,
.portal-app .table-container .app-data-table .v-table > thead > tr > th {
  background: var(--y-table-header-bg-solid) !important;
  background-color: var(--y-table-header-bg-solid) !important;
}

.portal-app .table-container .app-data-table tbody td {
  position: relative;
  z-index: 0;
  padding: 0 var(--y-space-2) !important;
  height: var(--y-table-row-height);
  font-size: var(--y-font-size-xs);
  line-height: var(--y-table-row-height);
  color: var(--y-table-body-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-bottom: 1px solid var(--y-table-row-border) !important;
  transition: background var(--y-transition-fast);
}

.portal-app .table-container .app-data-table tbody tr:nth-child(even) td {
  background: var(--y-table-row-stripe);
}

.portal-app .table-container .app-data-table tbody tr:hover td {
  background: var(--y-table-row-hover) !important;
}

.table-container .app-data-table .v-table__wrapper {
  isolation: isolate;
}

.table-container .app-data-table thead {
  position: sticky;
  top: 0;
  z-index: 12;
  background: var(--y-table-header-bg-solid, var(--y-table-header-bg)) !important;
}

.table-container .app-data-table thead th {
  position: sticky;
  top: 0;
  z-index: 12;
  padding: 0 var(--y-space-2) !important;
  height: var(--y-table-header-height) !important;
  min-height: var(--y-table-header-height) !important;
  font-size: 0.6875rem !important;
  font-weight: 600 !important;
  line-height: var(--y-table-header-height) !important;
  text-transform: uppercase;
  letter-spacing: var(--y-letter-spacing-label);
  color: var(--y-table-header-text) !important;
  background-color: var(--y-table-header-bg-solid, var(--y-table-header-bg)) !important;
  background-image: none !important;
  border-bottom: 1px solid var(--y-border-strong) !important;
  white-space: nowrap;
  vertical-align: middle;
  overflow: visible;
  box-shadow: var(--y-table-header-shadow);
}

.portal-app .table-container .app-data-table thead th .v-icon,
.table-container .app-data-table thead th .v-icon {
  color: var(--y-table-header-text) !important;
  opacity: 1 !important;
}

.table-container .app-data-table thead th,
.table-container .app-data-table thead th .v-data-table-header__content,
.table-container .app-data-table thead th .v-data-table-header__content span,
.table-container .app-data-table thead th.text-medium-emphasis,
.table-container .app-data-table thead th .text-medium-emphasis {
  color: var(--y-table-header-text) !important;
  -webkit-text-fill-color: var(--y-table-header-text) !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.table-container .app-data-table tbody td {
  padding: 0 var(--y-space-2) !important;
  height: var(--y-table-row-height);
  font-size: var(--y-font-size-xs);
  line-height: var(--y-table-row-height);
  color: var(--y-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-bottom: 1px solid var(--y-border) !important;
  transition: background var(--y-transition-fast);
}

.table-container .app-data-table tbody tr:nth-child(even) td {
  background: var(--y-table-row-stripe);
}

.table-container .app-data-table tbody tr:hover td {
  background: var(--y-table-row-hover) !important;
}

.table-container .app-data-table .table-chip {
  max-width: 100%;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.table-container .app-data-table .table-chip .v-chip__content {
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-container .app-data-table tbody tr.v-data-table-rows-no-data td {
  text-align: center !important;
  padding: var(--y-space-6) var(--y-space-2) !important;
  height: auto !important;
  line-height: var(--y-line-height-normal) !important;
  white-space: normal !important;
  color: var(--y-text-muted);
}

.table-container .app-data-table tbody tr.v-data-table-rows-loading td {
  padding: 0 !important;
  height: auto !important;
  line-height: normal !important;
  white-space: normal !important;
  text-align: left !important;
  vertical-align: top !important;
  border-bottom: none !important;
  background: var(--y-bg-subtle, #f1f5f9) !important;
}

.portal-app .table-container .app-data-table tbody tr.v-data-table-rows-no-data td {
  color: var(--y-table-empty-subtitle);
}

.portal-app .table-container .app-data-table tbody tr.v-data-table-rows-loading td {
  background: var(--y-table-header-bg-solid) !important;
}

/* Vuetify 3 renders header loading as thead > tr.v-data-table-progress (not __progress) */
.table-container .app-data-table thead tr.v-data-table-progress,
.portal-app .table-container .app-data-table thead tr.v-data-table-progress {
  display: none !important;
  height: 0 !important;
  visibility: hidden !important;
  pointer-events: none !important;
}

.table-container .app-data-table thead tr.v-data-table-progress th,
.table-container .app-data-table .v-data-table-progress__loader,
.table-container .app-data-table .v-data-table-progress__loader .v-progress-linear,
.portal-app .table-container .app-data-table thead tr.v-data-table-progress th,
.portal-app .table-container .app-data-table .v-data-table-progress__loader,
.portal-app
  .table-container
  .app-data-table
  .v-data-table-progress__loader
  .v-progress-linear {
  display: none !important;
  height: 0 !important;
  min-height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden !important;
}

.table-container .app-data-table.v-data-table--loading thead tr:not(.v-data-table-progress) th,
.portal-app
  .table-container
  .app-data-table.v-data-table--loading
  thead
  tr:not(.v-data-table-progress)
  th {
  opacity: 1 !important;
  visibility: visible !important;
}

.table-container .app-data-table th.col-actions,
.table-container .app-data-table td.col-actions {
  width: 3.25rem !important;
  max-width: 3.25rem !important;
  min-width: 3.25rem !important;
  padding-left: var(--y-space-1) !important;
  padding-right: var(--y-space-1) !important;
  text-align: center !important;
}

.table-container .app-data-table .table-actions {
  justify-content: flex-end;
  gap: 4px;
}

.table-container .app-data-table .col-actions .table-actions {
  justify-content: center;
}

.table-action-icon {
  cursor: pointer;
  opacity: 0.88;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.table-action-icon:hover {
  opacity: 1;
  transform: scale(1.08);
}

.table-action-icon--edit {
  color: #16a34a !important;
}

.table-action-icon--delete {
  color: #ef4444 !important;
}

.portal-app .table-container .app-data-table tbody td .table-action-icon {
  -webkit-text-fill-color: currentColor !important;
}

/* Loading: fade stale body cells only — never touch thead */
.table-container .app-data-table.v-data-table--loading tbody tr:not(.v-data-table-rows-loading) td {
  opacity: 0.2;
  pointer-events: none;
}

.table-container .app-data-table thead,
.table-container .app-data-table thead th {
  visibility: visible !important;
  opacity: 1 !important;
}

.table-container .app-data-table tbody tr.v-data-table-rows-loading {
  pointer-events: none;
}

.app-data-table-skeleton {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: #f1f5f9;
  padding: 2px 0;
}

.app-data-table-skeleton__row {
  display: grid;
  grid-template-columns: var(
    --sk-grid-cols,
    repeat(var(--sk-cols, 5), minmax(0, 1fr))
  );
  gap: 0;
  align-items: center;
  min-height: var(--y-table-row-height);
  padding: 0;
  box-sizing: border-box;
  border-bottom: 1px solid #e2e8f0;
}

.app-data-table-skeleton__cell {
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0 var(--y-space-2);
  box-sizing: border-box;
}

.app-data-table-skeleton__cell--center {
  justify-content: center;
}

.app-data-table-skeleton__cell--end {
  justify-content: flex-end;
}

.app-data-table-skeleton__bar {
  height: 10px;
  max-width: 100%;
  border-radius: var(--y-radius-sm);
  background: rgba(148, 163, 184, 0.35);
  animation: app-data-table-skeleton-pulse 1.4s ease-in-out infinite;
}

.portal-app .app-data-table-skeleton {
  background: var(--y-table-header-bg-solid);
}

.portal-app .app-data-table-skeleton__row {
  border-bottom-color: var(--y-table-row-border);
}

.portal-app .app-data-table-skeleton__bar {
  background: var(--y-skeleton-accent);
}

.portal-app .v-skeleton-loader {
  background: transparent !important;
}

.portal-app .v-skeleton-loader .v-skeleton-loader__bone {
  background: var(--y-skeleton-accent) !important;
}

@keyframes app-data-table-skeleton-pulse {
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 0.9;
  }
}

/* Empty state */
.portal-app .table-empty-state__icon-wrap {
  background: var(--y-table-empty-icon-bg);
}

.portal-app .table-empty-state__title {
  color: var(--y-table-empty-title);
}

.portal-app .table-empty-state__subtitle {
  color: var(--y-table-empty-subtitle);
}

.portal-app .text-medium-emphasis {
  color: var(--y-text-muted) !important;
}

.table-empty-state {
  padding: var(--y-space-8) var(--y-space-4);
  text-align: center;
}

.table-empty-state__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin-bottom: var(--y-space-4);
  border-radius: var(--y-radius-full);
  background: var(--y-primary-muted);
}

.table-empty-state__title {
  font-size: var(--y-font-size-md);
  font-weight: 600;
  color: var(--y-text);
  margin-bottom: var(--y-space-1);
}

.table-empty-state__subtitle {
  font-size: var(--y-font-size-sm);
  color: var(--y-text-muted);
  margin-bottom: var(--y-space-4);
}

/* SIP numbers — auto layout so Created/Actions shrink to content (no gap) */
.table-container .sip-numbers-table .v-table__wrapper > table {
  table-layout: auto;
}

.table-container .sip-numbers-table .col-sip,
.table-container .sip-numbers-table th:nth-child(1),
.table-container .sip-numbers-table td:nth-child(1) {
  max-width: 11rem;
}

.table-container .sip-numbers-table .col-created,
.table-container .sip-numbers-table th:nth-child(7),
.table-container .sip-numbers-table td:nth-child(7) {
  width: 1%;
  white-space: nowrap;
  padding-right: var(--y-space-2) !important;
}

.table-container .sip-numbers-table .col-actions,
.table-container .sip-numbers-table th:nth-child(8),
.table-container .sip-numbers-table td:nth-child(8) {
  width: 1%;
  white-space: nowrap;
  padding-left: var(--y-space-1) !important;
  padding-right: var(--y-space-2) !important;
}

/* ── Pagination footer ── */
.portal-app .table-container .app-data-table .v-data-table-footer {
  flex-shrink: 0;
  min-height: 40px;
  padding: var(--y-space-1) var(--y-space-3);
  background: var(--y-table-footer-bg) !important;
  border-top: 1px solid var(--y-table-border);
  font-size: var(--y-font-size-xs);
  color: var(--y-text-muted);
}

.portal-app .table-container .app-data-table .v-pagination .v-btn {
  background: var(--y-glass-input-bg) !important;
  color: var(--y-text-secondary) !important;
}

.portal-app .table-container .app-data-table .v-pagination .v-btn--disabled {
  opacity: 0.4;
}

.table-container .app-data-table .v-data-table-footer {
  flex-shrink: 0;
  min-height: 40px;
  padding: var(--y-space-1) var(--y-space-3);
  background: var(--y-surface);
  border-top: 1px solid var(--y-border);
  font-size: var(--y-font-size-xs);
  color: var(--y-text-secondary);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--y-space-2);
  row-gap: var(--y-space-1);
}

.portal-app .table-container .app-data-table .v-data-table-footer__items-per-page > span,
.portal-app .table-container .app-data-table .v-data-table-footer__info,
.portal-app .table-container .app-data-table .v-data-table-footer__page {
  color: var(--y-text-muted);
}

.table-container .app-data-table .v-data-table-footer__items-per-page > span {
  font-size: var(--y-font-size-sm);
  padding-inline-end: var(--y-space-2);
  color: var(--y-text-secondary);
}

.table-container .app-data-table .v-data-table-footer__items-per-page > .v-select {
  width: 80px;
}

.portal-app
  .table-container
  .app-data-table
  .v-data-table-footer__items-per-page
  > .v-select
  .v-field {
  background: var(--y-glass-input-bg) !important;
  color: var(--y-field-text);
}

.portal-app
  .table-container
  .app-data-table
  .v-data-table-footer__items-per-page
  > .v-select
  .v-field__input {
  color: var(--y-field-text) !important;
}

.table-container
  .app-data-table
  .v-data-table-footer__items-per-page
  > .v-select
  .v-field {
  min-height: 28px;
  font-size: var(--y-font-size-xs);
  border-radius: var(--y-radius-sm);
}

.table-container
  .app-data-table
  .v-data-table-footer__items-per-page
  > .v-select
  .v-field__input {
  min-height: 28px;
  padding-top: 0;
  padding-bottom: 0;
  font-size: var(--y-font-size-xs);
}

.table-container .app-data-table .v-data-table-footer__info {
  min-width: 96px;
  padding: 0 var(--y-space-3);
  font-size: var(--y-font-size-sm);
  font-weight: 500;
}

.table-container .app-data-table .v-data-table-footer__items-per-page,
.table-container .app-data-table .v-data-table-footer__info,
.table-container .app-data-table .v-data-table-footer__pagination,
.table-container .app-data-table .v-data-table-footer__page {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

.table-container .app-data-table .v-data-table-footer__pagination {
  margin-inline-start: 0;
}

.table-container .app-data-table .v-data-table-footer__page {
  padding: 0 var(--y-space-1);
  font-size: var(--y-font-size-sm);
}

.table-container .app-data-table .v-pagination .v-btn {
  width: 28px;
  height: 28px;
  font-size: var(--y-font-size-xs);
  border-radius: var(--y-radius-sm);
}

.table-container .app-data-table .v-pagination__item,
.table-container .app-data-table .v-pagination__prev,
.table-container .app-data-table .v-pagination__next,
.table-container .app-data-table .v-pagination__first,
.table-container .app-data-table .v-pagination__last {
  min-width: 28px;
  height: 28px;
}

/* Primary CTA buttons in toolbars */
.btn-primary-cta {
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
}

/* Enforce one CTA size even when size="small" is set on v-btn */
.portal-app .btn-primary-cta.v-btn--size-small,
.v-application .btn-primary-cta.v-btn--size-small {
  min-height: var(--y-btn-cta-height, 40px) !important;
  height: var(--y-btn-cta-height, 40px) !important;
  font-size: var(--y-btn-cta-font-size, 0.875rem) !important;
}

/* Filter selects in toolbar */
.table-filter-select {
  max-width: 152px !important;
  min-width: 132px !important;
}
</style>
